import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { SignatureSetResult } from "@probetalab/contracts";
import { get, run } from "../lib/api.js";
import { fmtDateTime } from "../lib/format.js";
import { useSession } from "../lib/session.js";
import { blobToBase64, prepareSignature, signatureFromFile, type SignatureResult } from "../lib/signature.js";
import { useToast } from "../lib/toast.js";
import { errorMessage, useQuery } from "../lib/useQuery.js";
import { Alert, Button, Card, Empty, Loading, PageHead, Tabs } from "../components/ui.js";

/**
 * "Mi firma": the validator's own digitised signature, printed over the rule of every report
 * she validates (NOM-007 4.7: "autógrafa, digitalizada o electrónica"). Two ways in: draw it
 * with the S Pen (or a finger, or a mouse) or photograph it on paper. Either way the image is
 * cleaned on the device — black ink on transparency, cropped to the ink — and previewed the way
 * the report prints it before it is saved. Only one's own signature; nobody sets another's.
 */

type Mode = "draw" | "photo";

interface Pending {
  blob: Blob;
  url: string;
}

export function MySignaturePage() {
  const { me } = useSession();
  const toast = useToast();
  const current = useQuery(() => get("mySignature"), []);
  const [mode, setMode] = useState<Mode>("draw");
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => () => {
    if (pending) URL.revokeObjectURL(pending.url);
  }, [pending]);

  if (!me) return null;
  const name = me.user.full_name;
  const cedula = me.user.cedula;

  if (!me.user.can_validate || me.operator) {
    return (
      <>
        <PageHead title="Mi firma" />
        <Empty icon="pen" title="Tu usuario no valida resultados" hint="La firma se imprime en los reportes de quien valida. Si validas, pide al Administrador que te dé el permiso." />
      </>
    );
  }

  const offer = (result: SignatureResult) => {
    setError(null);
    if ("problem" in result) {
      setError(
        result.problem === "unclear"
          ? "No pudimos separar la firma del papel: la foto tiene sombras o poca luz. Tómala de nuevo con luz pareja y sin sombra sobre la hoja, o dibújala en la pestaña Dibujar."
          : "No encontramos trazo en la imagen. Intenta con más luz y la firma en tinta oscura sobre papel blanco.",
      );
      return;
    }
    setPending({ blob: result.blob, url: URL.createObjectURL(result.blob) });
  };

  const save = async () => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      await run<"signature.set", SignatureSetResult>("signature.set", { png_base64: await blobToBase64(pending.blob) });
      toast.success("Firma guardada");
      setPending(null);
      current.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await run<"signature.set", SignatureSetResult>("signature.set", { png_base64: null });
      toast.success("Firma eliminada");
      current.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const stored = current.data?.signature ?? null;

  return (
    <>
      <PageHead title="Mi firma" lede="Se imprime sobre la línea de firma en los reportes que validas." />
      {error && <Alert tone="red" icon="warning">{error}</Alert>}

      <div className="grid-2" style={{ alignItems: "start" }}>
        <Card
          title="Firma actual"
          actions={
            stored ? (
              <Button size="sm" variant="ghost" icon="x" onClick={() => void remove()} disabled={busy}>
                Eliminar firma
              </Button>
            ) : null
          }
        >
          {current.loading && !current.data ? (
            <Loading />
          ) : (
            <div className="stack" style={{ gap: 12 }}>
              <SignaturePreview src={stored ? `data:image/png;base64,${stored.png_base64}` : null} alt="Firma actual" name={name} cedula={cedula} />
              <p className="muted" style={{ margin: 0, fontSize: "0.875rem" }}>
                {stored
                  ? `Guardada el ${fmtDateTime(stored.updated_at)}`
                  : "Todavía no tienes firma guardada: el reporte imprime tu nombre sobre la línea."}
              </p>
            </div>
          )}
        </Card>

        <Card title="Nueva firma">
          <div className="stack" style={{ gap: 16 }}>
            <Tabs
              value={mode}
              onChange={(m) => {
                setMode(m);
                setPending(null);
                setError(null);
              }}
              items={[
                { value: "draw", label: "Dibujar" },
                { value: "photo", label: "Subir foto" },
              ]}
            />
            {mode === "draw" ? <DrawPad onReady={offer} onClear={() => setPending(null)} disabled={busy} /> : <PhotoPicker onReady={offer} disabled={busy} />}
            {pending && (
              <div className="stack" style={{ gap: 12 }}>
                <div className="h3" style={{ margin: 0 }}>Así se verá en el reporte</div>
                <SignaturePreview src={pending.url} alt="Vista previa de la firma" name={name} cedula={cedula} />
                <div className="row wrap" style={{ gap: 10 }}>
                  <Button variant="primary" icon="check" onClick={() => void save()} busy={busy}>
                    Guardar firma
                  </Button>
                  <Button variant="ghost" onClick={() => setPending(null)} disabled={busy}>
                    Descartar
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
    </>
  );
}

/**
 * The report's signature block at screen scale: the image fitted in a 150 × 40 pt box (here
 * 300 × 80 px), bottom-aligned on the rule, the name and the cédula below. Without an image the
 * name in italic stands in, exactly as the PDF does.
 */
export function SignaturePreview({ src, alt, name, cedula }: { src: string | null; alt: string; name: string; cedula: string | null }) {
  return (
    <figure aria-label="Bloque de firma del reporte" style={{ margin: 0, padding: "18px 12px 12px", background: "#fff", border: "1px solid var(--line)", borderRadius: 10, display: "grid", justifyItems: "center", gap: 2 }}>
      <div style={{ width: 300, maxWidth: "100%", height: 80, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
        {src ? (
          <img src={src} alt={alt} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
        ) : (
          <span style={{ fontStyle: "italic", fontFamily: "Helvetica, Arial, sans-serif", fontSize: 18, color: "#000" }}>{name}</span>
        )}
      </div>
      <div style={{ width: 320, maxWidth: "100%", borderTop: "1.2px solid #000" }} />
      <span style={{ fontFamily: "Helvetica, Arial, sans-serif", fontSize: 15, color: "#000" }}>{name}</span>
      {cedula && <span style={{ fontFamily: "Helvetica, Arial, sans-serif", fontSize: 15, color: "#000" }}>Céd. Prof. {cedula}</span>}
    </figure>
  );
}

/**
 * Drawing surface for the S Pen (pointer events). Pressure widens the stroke when the device
 * reports it; once a pen has touched the pad, finger touches are ignored so a resting palm
 * leaves no marks. `touch-action: none` keeps the page from scrolling under the pen.
 */
function DrawPad({ onReady, onClear, disabled }: { onReady: (result: SignatureResult) => void; onClear: () => void; disabled: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const last = useRef<{ x: number; y: number } | null>(null);
  const penSeen = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const [working, setWorking] = useState(false);

  const point = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const c = ref.current!;
    const r = c.getBoundingClientRect();
    return { x: ((e.clientX - r.left) * c.width) / Math.max(1, r.width), y: ((e.clientY - r.top) * c.height) / Math.max(1, r.height) };
  };
  const accept = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (e.pointerType === "pen") penSeen.current = true;
    return !(e.pointerType === "touch" && penSeen.current);
  };
  const stroke = (from: { x: number; y: number }, to: { x: number; y: number }, e: ReactPointerEvent<HTMLCanvasElement>) => {
    const ctx = ref.current?.getContext("2d");
    if (!ctx) return;
    const pressure = e.pointerType === "pen" && e.pressure > 0 ? e.pressure : 0.5;
    ctx.strokeStyle = "#000";
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.5 + pressure * 5;
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
  };

  const clear = () => {
    const c = ref.current;
    c?.getContext("2d")?.clearRect(0, 0, c.width, c.height);
    setHasInk(false);
    onClear();
  };

  return (
    <div className="stack" style={{ gap: 10 }}>
      <canvas
        ref={ref}
        width={1200}
        height={400}
        aria-label="Área para dibujar la firma"
        style={{ width: "100%", aspectRatio: "3 / 1", background: "#fff", border: "1.5px dashed var(--line)", borderRadius: 10, touchAction: "none", cursor: "crosshair" }}
        onPointerDown={(e) => {
          if (disabled || !accept(e)) return;
          e.currentTarget.setPointerCapture?.(e.pointerId);
          const p = point(e);
          last.current = p;
          stroke(p, { x: p.x + 0.1, y: p.y + 0.1 }, e);
          setHasInk(true);
        }}
        onPointerMove={(e) => {
          if (!last.current || !accept(e)) return;
          const p = point(e);
          stroke(last.current, p, e);
          last.current = p;
        }}
        onPointerUp={() => (last.current = null)}
        onPointerCancel={() => (last.current = null)}
      />
      <div className="row wrap" style={{ gap: 10 }}>
        <Button
          variant="secondary"
          icon="check"
          disabled={!hasInk || disabled}
          busy={working}
          onClick={async () => {
            const c = ref.current;
            if (!c) return;
            setWorking(true);
            try {
              onReady(await prepareSignature(c, c.width, c.height, "drawing"));
            } finally {
              setWorking(false);
            }
          }}
        >
          Usar este trazo
        </Button>
        <Button variant="ghost" icon="refresh" onClick={clear} disabled={!hasInk || disabled}>
          Limpiar
        </Button>
      </div>
    </div>
  );
}

function PhotoPicker({ onReady, disabled }: { onReady: (result: SignatureResult) => void; disabled: boolean }) {
  const [working, setWorking] = useState(false);
  return (
    <div className="stack" style={{ gap: 10 }}>
      <p className="muted" style={{ margin: 0 }}>
        Firma en tinta oscura sobre una hoja blanca y tómale una foto de cerca, con buena luz. Quitamos el fondo y recortamos la firma.
      </p>
      <label className="btn btn-secondary" style={{ justifySelf: "start", position: "relative" }} aria-disabled={disabled || working}>
        {working ? "Procesando…" : "Elegir foto"}
        <input
          type="file"
          accept="image/*"
          aria-label="Foto de la firma"
          disabled={disabled || working}
          className="sr-only"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (!file) return;
            setWorking(true);
            try {
              onReady(await signatureFromFile(file));
            } catch {
              onReady({ problem: "empty" });
            } finally {
              setWorking(false);
            }
          }}
        />
      </label>
    </div>
  );
}
